import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { getUtm, track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20";
const labelCls = "mb-1.5 block text-sm font-semibold text-navy";
const hintCls = "mb-2 text-sm text-slate-500";

const TERMINOLOGY = ["Client", "Member", "Patient", "Consumer", "Other"];
const NOTE_FORMATS = [
  { v: "PIE", label: "PIE (Problem, Intervention, Evaluation)" },
  { v: "SOAP", label: "SOAP (Subjective, Objective, Assessment, Plan)" },
  { v: "DAP", label: "DAP (Data, Assessment, Plan)" },
  { v: "GIRP", label: "GIRP (Goal, Intervention, Response, Plan)" },
  { v: "Other", label: "Other/Custom" },
];
const AI_PLATFORMS = [
  "Google Workspace (Gemini)",
  "Microsoft Copilot",
  "ChatGPT (Enterprise/Team)",
  "Other",
  "We haven't selected one yet and would like guidance during our call.",
];

function RadioGroup({ name, options, value, onChange, testid }) {
  return (
    <div className="space-y-2.5" role="radiogroup" data-testid={testid}>
      {options.map((opt) => {
        const v = typeof opt === "string" ? opt : opt.v;
        const label = typeof opt === "string" ? opt : opt.label;
        return (
          <label key={v} className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 transition-colors hover:border-teal-400 has-[:checked]:border-teal-600 has-[:checked]:bg-teal-50/50">
            <input
              type="radio"
              name={name}
              value={v}
              checked={value === v}
              onChange={() => onChange(v)}
              className="mt-0.5 h-4 w-4 accent-teal-600"
              data-testid={`${testid}-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            />
            <span className="text-sm text-slate-700">{label}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function Onboarding() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [terminology, setTerminology] = useState("");
  const [noteFormat, setNoteFormat] = useState("");
  const [extraPrompts, setExtraPrompts] = useState("");
  const [aiPlatform, setAiPlatform] = useState("");
  const startedTracked = useRef(false);
  const mountedAt = useRef(Date.now());

  const handleFirstFocus = () => {
    if (!startedTracked.current) {
      startedTracked.current = true;
      track("demo_form_start", { source: "onboarding" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const f = e.currentTarget;
    const val = (n) => f.elements[n]?.value?.trim() || "";
    const withOther = (choice, otherVal) =>
      choice === "Other" && otherVal ? `Other: ${otherVal}` : choice;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/onboarding`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agency_name: val("agency_name"),
          contact_name: val("contact_name"),
          email: val("email"),
          team_type: val("team_type"),
          terminology: withOther(terminology, val("terminology_other")),
          note_format: withOther(noteFormat, val("note_format_other")),
          mandatory_verbiage: val("mandatory_verbiage"),
          formatting_rules: val("formatting_rules"),
          extra_prompts: extraPrompts,
          custom_prompt_details: val("custom_prompt_details"),
          ai_platform: withOther(aiPlatform, val("ai_platform_other")),
          pain_points: val("pain_points"),
          company_website: val("company_website"),
          elapsed_ms: Date.now() - mountedAt.current,
          ...getUtm(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === "string" ? data.detail : "Submission failed. Please try again.");
      }
      track("demo_form_submit", { source: "onboarding" });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-body text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <Link to="/" data-testid="onboarding-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-navy">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to VeraChart
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">VeraChart Onboarding</p>
        <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
          Agency Onboarding &amp; Prompt Customization
        </h1>
        <p className="mt-5 text-base leading-relaxed text-slate-600">
          Welcome to VeraChart! To ensure your prompt library is perfectly aligned with your agency's workflow,
          clinical standards, and billing requirements, please complete this brief questionnaire. We will use this
          information to customize your prompts prior to our live setup and training session.
        </p>

        {submitted ? (
          <div className="mt-10 flex flex-col items-start gap-4 rounded-xl border border-teal-200 bg-teal-50 p-8" data-testid="onboarding-success">
            <CheckCircle2 className="h-10 w-10 text-teal-600" aria-hidden="true" />
            <p className="font-display text-xl font-bold text-navy">Thank you — we've received your questionnaire.</p>
            <p className="text-slate-600">
              We'll use your answers to customize your prompt library before your live setup session. Questions in the
              meantime? <a href="mailto:sales@verachart.org" className="font-medium text-teal-700 underline underline-offset-2">sales@verachart.org</a>
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-12 space-y-12" data-testid="onboarding-form" onFocusCapture={handleFirstFocus}>
            <div aria-hidden="true" className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden">
              <label htmlFor="company_website_ob">Company website</label>
              <input id="company_website_ob" name="company_website" type="text" tabIndex={-1} autoComplete="off" />
            </div>

            <section aria-labelledby="ob-s1">
              <h2 id="ob-s1" className="font-display text-lg font-bold text-navy">Section 1: Agency &amp; Team Profile</h2>
              <p className="mt-1 text-sm text-slate-500">This helps us understand your specific scope of practice and team dynamics.</p>
              <div className="mt-6 space-y-6">
                <div>
                  <label htmlFor="agency_name" className={labelCls}>1. Agency / Organization Name *</label>
                  <input id="agency_name" name="agency_name" required maxLength={200} className={inputCls} data-testid="onboarding-agency-name" />
                </div>
                <div>
                  <label htmlFor="contact_name" className={labelCls}>2. Primary Contact Name &amp; Title *</label>
                  <input id="contact_name" name="contact_name" required maxLength={200} className={inputCls} data-testid="onboarding-contact-name" />
                </div>
                <div>
                  <label htmlFor="email" className={labelCls}>Primary Contact Work Email *</label>
                  <p className={hintCls}>We'll send your confirmation and scheduling details here.</p>
                  <input id="email" name="email" type="email" required maxLength={200} className={inputCls} data-testid="onboarding-email" autoComplete="email" />
                </div>
                <div>
                  <label htmlFor="team_type" className={labelCls}>3. What type of team or facility are these prompts for? *</label>
                  <p className={hintCls}>e.g., ACT, Peer Support, Intensive In-Home, Facility-based residential</p>
                  <input id="team_type" name="team_type" required maxLength={200} className={inputCls} data-testid="onboarding-team-type" />
                </div>
                <fieldset>
                  <legend className={labelCls}>4. Preferred Terminology *</legend>
                  <p className={hintCls}>How does your agency prefer to refer to the people you serve in official documentation?</p>
                  <RadioGroup name="terminology" options={TERMINOLOGY} value={terminology} onChange={setTerminology} testid="onboarding-terminology" />
                  {terminology === "Other" && (
                    <input name="terminology_other" placeholder="Your preferred term" required maxLength={100} className={`${inputCls} mt-3`} data-testid="onboarding-terminology-other" />
                  )}
                </fieldset>
              </div>
            </section>

            <section aria-labelledby="ob-s2">
              <h2 id="ob-s2" className="font-display text-lg font-bold text-navy">Section 2: Core Prompt Customization</h2>
              <p className="mt-1 text-sm text-slate-500">Tell us how your agency formats its standard documentation.</p>
              <div className="mt-6 space-y-6">
                <fieldset>
                  <legend className={labelCls}>5. Standard Note Format *</legend>
                  <p className={hintCls}>What documentation format does your agency mandate for Progress Notes?</p>
                  <RadioGroup name="note_format" options={NOTE_FORMATS} value={noteFormat} onChange={setNoteFormat} testid="onboarding-note-format" />
                  {noteFormat === "Other" && (
                    <input name="note_format_other" placeholder="Your format" required maxLength={100} className={`${inputCls} mt-3`} data-testid="onboarding-note-format-other" />
                  )}
                </fieldset>
                <div>
                  <label htmlFor="mandatory_verbiage" className={labelCls}>6. Mandatory Verbiage &amp; Compliance</label>
                  <p className={hintCls}>Specific phrases or sentences your state auditors or agency policies require in every note (e.g., "Interventions were provided to prevent higher level of care").</p>
                  <textarea id="mandatory_verbiage" name="mandatory_verbiage" rows={3} maxLength={2000} className={inputCls} data-testid="onboarding-mandatory-verbiage" />
                </div>
                <div>
                  <label htmlFor="formatting_rules" className={labelCls}>7. Specific Formatting Rules</label>
                  <p className={hintCls}>e.g., "Do not use bullet points," "Must include exact start and end times," "Must write in the third person"</p>
                  <textarea id="formatting_rules" name="formatting_rules" rows={3} maxLength={2000} className={inputCls} data-testid="onboarding-formatting-rules" />
                </div>
              </div>
            </section>

            <section aria-labelledby="ob-s3">
              <h2 id="ob-s3" className="font-display text-lg font-bold text-navy">Section 3: Extra Prompt Requests</h2>
              <p className="mt-1 text-sm text-slate-500">Beyond the core package (Progress Notes, Crisis Calls, Visit Attempts, Collateral Contacts, PCPs, CCAs).</p>
              <div className="mt-6 space-y-6">
                <fieldset>
                  <legend className={labelCls}>8. Do you require any additional, custom prompt templates? *</legend>
                  <p className={hintCls}>e.g., Incident Reports, Shift Summaries, Hospital Discharge Summaries</p>
                  <RadioGroup
                    name="extra_prompts"
                    options={[
                      { v: "No", label: "No, the core package is all we need right now." },
                      { v: "Yes", label: "Yes (please describe below)" },
                    ]}
                    value={extraPrompts}
                    onChange={setExtraPrompts}
                    testid="onboarding-extra-prompts"
                  />
                </fieldset>
                {extraPrompts === "Yes" && (
                  <div>
                    <label htmlFor="custom_prompt_details" className={labelCls}>9. Custom Prompt Details *</label>
                    <p className={hintCls}>List the name of the note/form and the specific sections it requires (e.g., "Incident Report: Location, Witnesses, Description of Event, De-escalation tactics used, Supervisor notified").</p>
                    <textarea id="custom_prompt_details" name="custom_prompt_details" rows={4} required maxLength={3000} className={inputCls} data-testid="onboarding-custom-details" />
                  </div>
                )}
              </div>
            </section>

            <section aria-labelledby="ob-s4">
              <h2 id="ob-s4" className="font-display text-lg font-bold text-navy">Section 4: Technology &amp; AI Setup</h2>
              <p className="mt-1 text-sm text-slate-500">This helps us prepare for your live onboarding and training session.</p>
              <div className="mt-6 space-y-6">
                <fieldset>
                  <legend className={labelCls}>10. Approved AI Platform *</legend>
                  <p className={hintCls}>Which AI platform has your agency approved for your team to use?</p>
                  <RadioGroup name="ai_platform" options={AI_PLATFORMS} value={aiPlatform} onChange={setAiPlatform} testid="onboarding-ai-platform" />
                  {aiPlatform === "Other" && (
                    <input name="ai_platform_other" placeholder="Your platform" required maxLength={100} className={`${inputCls} mt-3`} data-testid="onboarding-ai-platform-other" />
                  )}
                </fieldset>
                <div>
                  <label htmlFor="pain_points" className={labelCls}>11. Biggest documentation pain points</label>
                  <p className={hintCls}>What do you want to make sure these prompts solve?</p>
                  <textarea id="pain_points" name="pain_points" rows={3} maxLength={2000} className={inputCls} data-testid="onboarding-pain-points" />
                </div>
              </div>
            </section>

            {error && <p className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="onboarding-error">{error}</p>}

            <button
              type="submit"
              disabled={submitting || !terminology || !noteFormat || !extraPrompts || !aiPlatform}
              data-testid="onboarding-submit-button"
              className="inline-flex h-12 items-center justify-center rounded-full bg-teal-600 px-10 font-display text-base font-semibold text-white shadow-md transition-colors hover:bg-teal-700 disabled:opacity-50"
            >
              {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />Submitting…</>) : "Submit Questionnaire"}
            </button>
          </form>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white py-8">
        <p className="mx-auto max-w-3xl px-4 text-xs text-slate-400 sm:px-6 lg:px-8">© 2026 VeraChart. All rights reserved.</p>
      </footer>
    </div>
  );
}
